import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { translate } from '@/services/TranslationService';
import store, { dispatch } from '@/services/store';
import { NovovilleButton, Typography } from '@/components/novoville';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { withNOVOHeader } from '../sideMenu/HeaderHOC';
import AmountInput from './AmountInput';
import PaymentDetailsCard from './PaymentDetailsCard';
import PaymentWebView from './PaymentWebView';
import { getPaymentDetails, preparePayment } from './actions';

const PaymentConfirmation = ({ location, setHeaderTitle, ...props }) => {
  useEffect(() => {
    const title = translate('sideMenu.Payments');
    setHeaderTitle(title);
  }, []);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      if (!location) return;

      try {
        const urlParams = location.pathname.split('/');
        const paymentCode = urlParams[urlParams.length - 1];
        await dispatch(getPaymentDetails(paymentCode));
      } catch (e) {
        return props.history.push('/payments');
      }
    };

    fetchPaymentDetails();
  }, []);

  const { paymentDetails: storePaymentDetails } =
    useSelector((state) => state.paymentReducer) || {};

  const paymentDetails = location.state?.paymentDetails;

  const {
    created_at,
    type,
    code,
    recipientName,
    invoiceUrl,
    paid,
    amount_in_cents,
  } = { ...storePaymentDetails, ...paymentDetails } || {};

  const [amount, setAmount] = useState(amount_in_cents || 0);
  const [paymentUri, setPaymentUri] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handlePreparePayment = async () => {
    try {
      const uri = await dispatch(
        preparePayment({
          payment_id: paymentDetails.payment.id,
          amount,
        })
      );

      setPaymentUri(uri || null);
    } catch (error) {
      setPaymentUri(null);
      toast({
        title: translate('OTHER.errorOcurred'),
        description: error.data?.result || translate('paymentPreparationError'),
        variant: 'destructive',
      });
    }
  };

  if (paymentUri) {
    return (
      <PaymentWebView
        uri={paymentUri}
        onError={(error) => {
          setPaymentUri(null);
          toast({
            title: translate('OTHER.errorOcurred'),
            description: error,
            variant: 'destructive',
          });
        }}
        onSucccess={() => {
          setPaymentUri(null);
          toast({
            title: translate('PaymentCompleted'),
            variant: 'success',
            duration: 5000,
          });
          props.history.push('/');
        }}
      />
    );
  }

  if (!code) return;

  return (
    <div className="flex flex-1 sm:flex-grow-0 flex-col relative">
      <div className="flex flex-col sm:m-10 m-0 sm:max-h-[calc(100vh-10rem)] sm:py-8 py-2 sm:px-8 px-4 flex-1 h-fit bg-white rounded-sm">
        <div className="flex flex-1 flex-col *:mb-8 first:mb-6 max-w-md">
          <Typography variant="bold_20" className="text-gray-800 mb-4">
            {translate('PaymentConfirmation')}
          </Typography>
          <PaymentDetailsCard
            issueDate={created_at}
            type={type}
            rfCode={code}
            payerName={recipientName}
          />
          <AmountInput
            value={amount}
            onChange={setAmount}
            disabled={!!paid}
            className="mt-6"
          />
          {paid === 0 && (
            <Dialog
              open={isModalOpen}
              onOpenChange={setIsModalOpen}
              style={{ height: 'inherit' }}
            >
              <DialogTrigger asChild>
                <NovovilleButton
                  className="mt-6 sm:w-36 w-full"
                  variant="primary"
                  disabled={paid === 1}
                  onClick={() => {
                    setIsModalOpen(true);
                  }}
                >
                  {translate('MakeAPayment')}
                </NovovilleButton>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px] flex flex-col items-center text-center gap-4 p-6">
                <Typography variant="bold_20" className="text-gray-800">
                  {translate('PaymentConfirmation')}
                </Typography>
                <Typography
                  variant="regular_16"
                  className="text-gray-700"
                  dangerouslySetInnerHTML={{
                    __html: (
                      translate('PaymentConfirmationText') || ''
                    ).replace(
                      '%%amount%%',
                      `<strong>€${(amount / 100).toFixed(2)}</strong>`
                    ),
                  }}
                />

                <div className="flex flex-col w-full gap-3 mt-4">
                  <NovovilleButton
                    variant="primary"
                    className="w-full"
                    onClick={() => {
                      setIsModalOpen(false);
                      handlePreparePayment();
                    }}
                  >
                    {translate('YES')}
                  </NovovilleButton>
                  <NovovilleButton
                    variant="secondary"
                    className="w-full border border-gray-300"
                    onClick={() => {
                      setIsModalOpen(false);
                    }}
                  >
                    {translate('CANCEL')}
                  </NovovilleButton>
                </div>
              </DialogContent>
            </Dialog>
          )}
          {paid === 1 && (
            <NovovilleButton
              className="mt-6  w-full"
              variant="primary"
              onClick={() => {
                window.open(invoiceUrl, '_blank');
              }}
            >
              {translate('DownloadInvoice')}
            </NovovilleButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default withNOVOHeader(withRouter(PaymentConfirmation));
